import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ICreateJourneyCurrentUserDTO } from '../../Interfaces/PersonalData.interface';
import { RootState } from '../../store';
import { useCreateJourneyRecurrentUserMutation } from '../../services/personalData.service';
import { dynamicCustomEventTracking } from '../../lib/gtm';
import { saveUserInfo } from '../../features/user/user-slice';
import { useCookies } from 'react-cookie';
import { getPersistedSessionId } from '../../providers/AssistantUIProvider';

export type ICreateJourneyRecurrentDTO = {
  token: string;
};

const useCreateJourneyRecurrentUser = () => {
  const newrelic = (window as any).newrelic;
  const dispatch = useDispatch();
  const navegate = useNavigate();
  const [cookies] = useCookies(['_rdtrk']);
  const [hasSessionStorage, setHasSessionStorage] = useState<boolean>(true);
  const {
    product,
    calendar: { calendarSelected, doctorId },
    personalDataAddressInfo,
    schedule,
    queryString
  } = useSelector((state: RootState) => state);
  const [createJourneyRecurrent, { data: dataJourney, error: errorJourney, isLoading: isLoadingJourney }] =
    useCreateJourneyRecurrentUserMutation();
  const chatSupportSessionId = getPersistedSessionId();
  const payload: ICreateJourneyCurrentUserDTO = {
    productid: product.id,
    oyanaAddress: null,
    schedule: {
      calendarid: calendarSelected?.id || '',
      userId: doctorId || '',
      dateStart: schedule.dateStart ? new Date(schedule.dateStart).toISOString() : ''
    },
    howMeet: 'Outro (Recorrente)',
    optimizeExperimentId: '',
    talentacademy: '',
    metadata: {
      chatSupportSessionId,
      utms: {
        utm_campaign: queryString.utms.utm_campaign,
        utm_medium: queryString.utms.utm_medium,
        utm_source: queryString.utms.utm_source,
        utm_id: queryString.utms.utm_id,
        origin_source: queryString.utms.origin_source,
        origin_medium: queryString.utms.origin_medium
      }
    },
    marketingTracking: cookies._rdtrk
      ? {
          clientTrackingId: cookies._rdtrk
        }
      : null
  };

  const handleCreateJourneyRecurrent = async ({ token }: ICreateJourneyRecurrentDTO) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (typeof window.gtag !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.gtag('event', 'optimize.callback', {
        callback: (variantId: string, experimentId: string) => {
          payload.optimizeExperimentId = `${experimentId}-${variantId}`;
        }
      });
    }

    payload.oyanaAddress =
      personalDataAddressInfo.cep.length > 0
        ? {
            cep: personalDataAddressInfo.cep.replace(/[^0-9]/g, ''),
            city: personalDataAddressInfo.city,
            state: personalDataAddressInfo.state?.toUpperCase(),
            address: personalDataAddressInfo.address,
            neighborhood: personalDataAddressInfo.neighborhood,
            addressNumber: personalDataAddressInfo.addressNumber || '',
            addressComplement: personalDataAddressInfo.addressComplement || '',
            addressReference: personalDataAddressInfo.addressReference || '',
            isResidentialAddress: personalDataAddressInfo.isResidentialAddress
          }
        : null;
    createJourneyRecurrent({
      token,
      data: { ...payload }
    });
  };

  useEffect(() => {
    if (product.id.length === 0) {
      console.error('Produto não encontrado para criação de jornada recorrente');
      newrelic?.noticeError(`[Agendamento] sessão expirada`);
      setHasSessionStorage(false);
    }
  }, [product.id]);

  useEffect(() => {
    if (dataJourney) {
      dynamicCustomEventTracking.send({ dynamicEventName: `journey_quick_access_create_journey_recurrent_ag` });
      dispatch(
        saveUserInfo({
          jorneyId: dataJourney.id,
          oyana: { id: dataJourney.oyana.id }
        })
      );
      if (dataJourney?.status === 'AWAITING_PAYMENT') {
        navegate('/opcoes-de-pagamento');
      }
    }
  }, [dataJourney, isLoadingJourney]);

  useEffect(() => {
    if (errorJourney) {
      dynamicCustomEventTracking.send({ dynamicEventName: `journey_quick_access_create_journey_recurrent_error_ag` });
    }
  }, [errorJourney]);

  return {
    handleCreateJourneyRecurrent,
    isLoadingJourney,
    errorJourney,
    dataJourney,
    hasSessionStorage
  };
};

export default useCreateJourneyRecurrentUser;
